import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, FileSpreadsheet, FileText, File } from 'lucide-react';
import * as XLSX from 'xlsx';

interface ExportButtonProps {
  data: any[];
  filename: string;
  columns: { key: string; label: string; format?: (value: any) => any }[];
  title?: string;
}

export function ExportButton({ data, filename, columns, title = 'بيانات' }: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const exportToExcel = () => {
    try {
      const exportData = data.map((item) => {
        const row: any = {};
        columns.forEach((col) => {
          const value = item[col.key];
          row[col.label] = col.format ? col.format(value) : (value ?? '');
        });
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, title);
      
      // ضبط عرض الأعمدة
      ws['!cols'] = columns.map(() => ({ wch: 20 }));
      
      XLSX.writeFile(wb, `${filename}.xlsx`);
      setIsOpen(false);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('حدث خطأ أثناء التصدير');
    }
  };

  const exportToCSV = () => {
    try {
      const headers = columns.map((col) => col.label).join(',');
      const rows = data.map((item) => {
        return columns.map((col) => {
          const value = item[col.key];
          const formatted = col.format ? col.format(value) : (value ?? '');
          // معالجة القيم التي تحتوي على فواصل أو علامات اقتباس
          const strValue = String(formatted);
          if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
            return `"${strValue.replace(/"/g, '""')}"`;
          }
          return strValue;
        }).join(',');
      });

      // إضافة BOM لدعم العربية في Excel
      const BOM = '﻿';
      const csvContent = BOM + headers + '\n' + rows.join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsOpen(false);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('حدث خطأ أثناء التصدير');
    }
  };

  const exportToPrint = () => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('يرجى السماح بالنوافذ المنبثقة');
        return;
      }

      const headers = columns.map((col) => `<th style="border:1px solid #ddd;padding:8px;background:#f5f5f5;text-align:right;">${col.label}</th>`).join('');
      const rows = data.map((item) => {
        const cells = columns.map((col) => {
          const value = item[col.key];
          const formatted = col.format ? col.format(value) : (value ?? '');
          return `<td style="border:1px solid #ddd;padding:8px;text-align:right;">${formatted}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      const html = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: rtl; }
    h1 { color: #ea580c; border-bottom: 2px solid #ea580c; padding-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
    th { background: #f5f5f5; font-weight: bold; }
    tr:nth-child(even) { background: #f9f9f9; }
    .footer { margin-top: 30px; text-align: center; color: #666; font-size: 11px; }
    @media print {
      body { padding: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <p>تاريخ الطباعة: ${new Date().toLocaleString('ar-EG')}</p>
  <p>إجمالي السجلات: ${data.length}</p>
  <table>
    <thead><tr>${headers}</tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">
    <p>Target Express - نظام إدارة الشحنات</p>
    <button class="no-print" onclick="window.print()" style="padding:10px 20px;background:#ea580c;color:white;border:none;border-radius:5px;cursor:pointer;margin-top:20px;">طباعة / حفظ PDF</button>
  </div>
  <script>setTimeout(() => window.print(), 500);</script>
</body>
</html>`;

      printWindow.document.write(html);
      printWindow.document.close();
      setIsOpen(false);
    } catch (error) {
      console.error('Error exporting to print:', error);
      alert('حدث خطأ أثناء التصدير');
    }
  };

  return (
    <div className="relative inline-block">
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2"
      >
        <Download className="w-4 h-4" />
        تصدير
      </Button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
            <button
              onClick={exportToExcel}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-right transition"
            >
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-semibold text-sm">Excel (.xlsx)</p>
                <p className="text-xs text-gray-500">ملف إكسل قابل للتعديل</p>
              </div>
            </button>
            <div className="border-t"></div>
            <button
              onClick={exportToCSV}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-right transition"
            >
              <File className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-semibold text-sm">CSV (.csv)</p>
                <p className="text-xs text-gray-500">ملف نصي مفصول بفواصل</p>
              </div>
            </button>
            <div className="border-t"></div>
            <button
              onClick={exportToPrint}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-right transition"
            >
              <FileText className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-semibold text-sm">PDF (طباعة)</p>
                <p className="text-xs text-gray-500">طباعة أو حفظ كـ PDF</p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
